/**
 * Technical-context capture: console errors/warnings, uncaught errors,
 * unhandled rejections, failed fetch/XHR requests and the SPA route trail.
 *
 * Installed only for activated browsers (or "everyone" sites), never for
 * ordinary production visitors. Every wrapper calls the original first and
 * swallows its own failures, so a bug here can never break the host page.
 * The loader's sourcemap marks this file in `ignoreList`, so devtools keep
 * attributing console calls to the page's own code, not to loader.js.
 */

import type { ConsoleEntry, NetworkFailure, RouteEntry } from "@/lib/site-feedback/shared/sanitize";
import { LIMITS } from "@/lib/site-feedback/shared/vocabulary";
import type { CapturedContext } from "../runtime";

const consoleEntries: ConsoleEntry[] = [];
const networkFailures: NetworkFailure[] = [];
const routes: RouteEntry[] = [];
let installed = false;
let ignoreOrigin = "";

function push<T>(list: T[], item: T, cap: number): void {
  list.push(item);
  if (list.length > cap) list.splice(0, list.length - cap);
}

function describeArg(arg: unknown): string {
  try {
    if (typeof arg === "string") return arg;
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
    if (arg === undefined) return "undefined";
    if (typeof arg === "object" && arg !== null) return JSON.stringify(arg).slice(0, 500);
    return String(arg);
  } catch {
    return "[unserializable]";
  }
}

function recordConsole(level: ConsoleEntry["level"], args: unknown[], stack?: string): void {
  try {
    const message = args.map(describeArg).join(" ").slice(0, LIMITS.entryChars);
    if (!message) return;
    const entry: ConsoleEntry = { level, message, at: Date.now() };
    const errorWithStack = args.find((a): a is Error => a instanceof Error && typeof a.stack === "string");
    const trace = stack ?? errorWithStack?.stack;
    if (trace) entry.stack = trace.slice(0, LIMITS.entryChars);
    push(consoleEntries, entry, LIMITS.consoleEntries);
  } catch {
    /* never break the page */
  }
}

function recordNetwork(method: string, url: string, status: number, startedAt: number): void {
  try {
    if (ignoreOrigin && url.startsWith(ignoreOrigin)) return;
    push(
      networkFailures,
      { method: method.toUpperCase(), url: new URL(url, location.href).href, status, durationMs: Math.round(performance.now() - startedAt), at: Date.now() },
      LIMITS.networkFailures,
    );
  } catch {
    /* ignore */
  }
}

function recordRoute(): void {
  try {
    const url = location.href;
    if (routes[routes.length - 1]?.url === url) return;
    push(routes, { url, at: Date.now() }, LIMITS.routeTrail);
  } catch {
    /* ignore */
  }
}

export function installHooks(dashOrigin: string): void {
  if (installed) return;
  installed = true;
  ignoreOrigin = dashOrigin;
  recordRoute();

  for (const level of ["error", "warn"] as const) {
    const original = console[level];
    console[level] = function sparkFeedbackConsole(this: unknown, ...args: unknown[]) {
      original.apply(this, args);
      recordConsole(level, args);
    };
  }

  window.addEventListener(
    "error",
    (event) => {
      const target = event.target as (HTMLElement & { src?: string; href?: string }) | null;
      if (target && target !== (window as unknown) && (target.src || target.href)) {
        // A sub-resource (image, script, stylesheet) failed to load.
        recordNetwork("GET", target.src || target.href || "", 0, performance.now());
        return;
      }
      recordConsole("uncaught", [event.message || "Uncaught error"], event.error?.stack);
    },
    true,
  );
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    recordConsole("rejection", [reason instanceof Error ? reason : describeArg(reason)], reason?.stack);
  });

  const originalFetch = window.fetch;
  if (typeof originalFetch === "function") {
    window.fetch = function sparkFeedbackFetch(this: unknown, input: RequestInfo | URL, init?: RequestInit) {
      const startedAt = performance.now();
      let method = "GET";
      let url = "";
      try {
        method = init?.method || (input instanceof Request ? input.method : "GET");
        url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      } catch {
        /* ignore */
      }
      return originalFetch.call(this, input as RequestInfo, init).then(
        (response) => {
          if (!response.ok) recordNetwork(method, url, response.status, startedAt);
          return response;
        },
        (error: unknown) => {
          recordNetwork(method, url, 0, startedAt);
          throw error;
        },
      );
    } as typeof window.fetch;
  }

  const proto = XMLHttpRequest.prototype;
  const originalOpen = proto.open;
  const originalSend = proto.send;
  const meta = new WeakMap<XMLHttpRequest, { method: string; url: string; startedAt: number }>();
  proto.open = function sparkFeedbackOpen(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    try {
      meta.set(this, { method, url: String(url), startedAt: 0 });
    } catch {
      /* ignore */
    }
    return (originalOpen as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof proto.open;
  proto.send = function sparkFeedbackSend(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const info = meta.get(this);
    if (info) {
      info.startedAt = performance.now();
      this.addEventListener("loadend", () => {
        if (this.status === 0 || this.status >= 400) recordNetwork(info.method, info.url, this.status, info.startedAt);
      });
    }
    return originalSend.call(this, body);
  };

  for (const name of ["pushState", "replaceState"] as const) {
    const original = history[name];
    history[name] = function sparkFeedbackHistory(this: History, ...args: Parameters<History["pushState"]>) {
      const result = original.apply(this, args);
      recordRoute();
      return result;
    };
  }
  window.addEventListener("popstate", recordRoute);
  window.addEventListener("hashchange", recordRoute);
}

/** Failed sub-resources the browser saw before the hooks were installed (Chromium). */
function backfillFromPerformance(): NetworkFailure[] {
  try {
    const entries = performance.getEntriesByType("resource") as Array<PerformanceResourceTiming & { responseStatus?: number }>;
    return entries
      .filter((e) => typeof e.responseStatus === "number" && e.responseStatus >= 400 && !(ignoreOrigin && e.name.startsWith(ignoreOrigin)))
      .slice(-LIMITS.networkFailures)
      .map((e) => ({
        method: "GET",
        url: e.name,
        status: e.responseStatus as number,
        durationMs: Math.round(e.duration),
        at: Math.round(performance.timeOrigin + e.startTime),
      }));
  } catch {
    return [];
  }
}

export function capturedContext(): CapturedContext {
  recordRoute();
  const seen = new Set(networkFailures.map((n) => `${n.url}|${n.status}`));
  const backfill = backfillFromPerformance().filter((n) => !seen.has(`${n.url}|${n.status}`));
  const network = [...backfill, ...networkFailures].sort((a, b) => a.at - b.at).slice(-LIMITS.networkFailures);
  return { console: [...consoleEntries], network, routes: [...routes] };
}
